import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField, InputAdornment, IconButton } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import "../styles/riskDashboard.scss";
import goIcon from "../../../../../assets/icons/right_arrow_go_btn.svg";
import RiskStratificationService from "../service/RiskStratificationService";
import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import GlobalSkeletonLoader from "components/common/GlobalSkeletonLoader";
import ColumnFilterPopover from "components/common/ColumnFilterPopover";
import moment from "moment";
import filterApplied from "../../../../../assets/icons/filter-applied-icon.svg";
import funnelIcon from "../../../../../assets/icons/funnel.svg";
import Button from "react-md/lib/Buttons/Button";
import FontIcon from "react-md/lib/FontIcons";
import exportIcon from "../../../../../assets/icons/export-icon-white.svg";
import eyeOrangeIcon from "../../../../../assets/icons/eye-orange.svg";
import backArrowIcon from "../../../../../assets/icons/back_left_arrow_orange.png";
import starRedIcon from "../../../../../assets/icons/star_red_icon.png";
import personOrangeIcon from "../../../../../assets/icons/person_icon_orange.png";
import RangeSlider from "../../service-module/helper/RangeSlider";

const riskStratificationService = new RiskStratificationService();
const authenticationService = new AuthenticationService();

const RISK_LEVEL_CLASS_MAP = {
  1: "life-threatening",
  2: "very-high",
  3: "high",
  4: "moderate",
  5: "low",
};

const RISK_LEVEL_LABEL_MAP = {
  1: "Life Threatening Risk",
  2: "Very High Risk",
  3: "High Risk",
  4: "Moderate Risk",
  5: "Low Risk",
};

function getConfidenceClass(score) {
  if (score >= 90) return "high";
  if (score >= 70) return "medium";
  return "low";
}

const EVAL_STATUS_LABEL_MAP = {
  1: "Pending",
  2: "Under Review",
  3: "Correct",
  4: "Incorrect",
};

const EVAL_STATUS_CLASS_MAP = {
  1: "pending",
  2: "under-review",
  3: "correct",
  4: "incorrect",
};

function getEvaluationStatusClass(status) {
  return EVAL_STATUS_CLASS_MAP[status] || "pending";
}

function getEvaluationStatusLabel(status) {
  return EVAL_STATUS_LABEL_MAP[status] || "Pending";
}

const isAwaitingEvaluation = (status) => status === 1 || status === 2;

const AiAssessmentTooltip = withStyles({
  tooltip: {
    backgroundColor: "#2A2F45",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  arrow: { color: "#2A2F45" },
})(Tooltip);

function RiskDashboard(props) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(20);
  const [exporting, setExporting] = useState(false);
  const [componentMounted, setComponentMounted] = useState(false);

  // Filter states
  const [openFilter, setOpenFilter] = useState(0);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  // Column filters
  //
  // Multi-select filters carry a `mode` ("include" | "exclude") in addition to
  // the chosen items. Mode flips when the user clicks "Invert" in the popover —
  // sending {mode: "exclude", values: [...]} lets the BE express "everything
  // except these" without the FE having to enumerate every id in the dataset.
  const [riskLevelFilter, setRiskLevelFilter] = useState(false);
  const [riskLevelFilterValue, setRiskLevelFilterValue] = useState([]);
  const [riskLevelFilterMode, setRiskLevelFilterMode] = useState("include");

  const [evaluationStatusFilter, setEvaluationStatusFilter] = useState(false);
  const [evaluationStatusFilterValue, setEvaluationStatusFilterValue] =
    useState([]);

  const [evaluatorFilter, setEvaluatorFilter] = useState(false);
  const [evaluatorFilterValue, setEvaluatorFilterValue] = useState([]);
  const [evaluatorFilterMode, setEvaluatorFilterMode] = useState("include");

  const [confidenceFilter, setConfidenceFilter] = useState(false);
  const [confidenceMin, setConfidenceMin] = useState("");
  const [confidenceMax, setConfidenceMax] = useState("");
  const [appliedConfidenceMin, setAppliedConfidenceMin] = useState("");
  const [appliedConfidenceMax, setAppliedConfidenceMax] = useState("");

  const [riskDateFrom, setRiskDateFrom] = useState("");
  const [riskDateTo, setRiskDateTo] = useState("");

  const [sourceDateFrom, setSourceDateFrom] = useState("");
  const [sourceDateTo, setSourceDateTo] = useState("");

  // Derive icon state from the dates themselves so the filled-funnel reflects
  // whichever popup applied the range (top pill or column header).
  const riskDateFilter = !!(riskDateFrom && riskDateTo);
  const sourceDateFilter = !!(sourceDateFrom && sourceDateTo);

  const [triggeredByFilter, setTriggeredByFilter] = useState(false);
  const [triggeredByFilterValue, setTriggeredByFilterValue] = useState([]);
  const [triggeredByFilterMode, setTriggeredByFilterMode] = useState("include");

  const [patientIdFilterText, setPatientIdFilterText] = useState("");
  const [appliedPatientId, setAppliedPatientId] = useState("");

  const [accountCodeFilter, setAccountCodeFilter] = useState(false);
  const [accountCodeFilterValue, setAccountCodeFilterValue] = useState([]);
  const [accountCodeFilterMode, setAccountCodeFilterMode] = useState("include");

  const accountCodeFilterIconRef = useRef(null);
  const riskLevelFilterIconRef = useRef(null);
  const evaluatorFilterIconRef = useRef(null);
  const triggeredByFilterIconRef = useRef(null);

  // Evaluator assignment dialog
  const [assignDialogRecord, setAssignDialogRecord] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const currentEmployeeId =
    authenticationService.getUserProfile()?.employeeProfile?.id || 0;

  const navigateToDetail = (record) => {
    if (!record || !props.history) return;
    const id = record.id || record.ID || record._id;
    if (!id) return;
    props.history.push(`/miscellaneous/risk-stratification/details/${id}`);
  };

  const handleEvaluatorPillClick = (record) => {
    if (record.evaluatedBy === currentEmployeeId) {
      navigateToDetail(record);
      return;
    }
    setAssignDialogRecord(record);
  };

  const handleEvaluateClick = (record) => {
    if (assigning) return;
    setAssigning(true);
    riskStratificationService.assignEvaluator(
      record.id,
      () => {
        setAssigning(false);
        navigateToDetail(record);
      },
      (err) => {
        setAssigning(false);
        console.error("Error assigning evaluator:", err);
        EventService.emit("showError", {
          message: "Failed to assign evaluator",
        });
      },
    );
  };

  const handleConfirmReassign = () => {
    if (!assignDialogRecord || assigning) return;
    const record = assignDialogRecord;
    setAssigning(true);
    riskStratificationService.assignEvaluator(
      record.id,
      () => {
        setAssigning(false);
        setAssignDialogRecord(null);
        navigateToDetail(record);
      },
      (err) => {
        setAssigning(false);
        console.error("Error assigning evaluator:", err);
        EventService.emit("showError", {
          message: "Failed to assign evaluator",
        });
      },
    );
  };

  const hasActiveFilters =
    riskLevelFilterValue.length > 0 ||
    evaluationStatusFilterValue.length > 0 ||
    evaluatorFilterValue.length > 0 ||
    triggeredByFilterValue.length > 0 ||
    accountCodeFilterValue.length > 0 ||
    !!appliedPatientId ||
    !!(appliedConfidenceMin && appliedConfidenceMax) ||
    !!(riskDateFrom && riskDateTo) ||
    !!(sourceDateFrom && sourceDateTo);

  // Resets every filter in a single batched render. The consolidated useEffect
  // sees the deps change and refetches once with the cleared body.
  const resetAllFilters = () => {
    setRiskLevelFilter(false);
    setRiskLevelFilterValue([]);
    setRiskLevelFilterMode("include");
    setEvaluationStatusFilter(false);
    setEvaluationStatusFilterValue([]);
    setEvaluatorFilter(false);
    setEvaluatorFilterValue([]);
    setEvaluatorFilterMode("include");
    setConfidenceFilter(false);
    setConfidenceMin("");
    setConfidenceMax("");
    setAppliedConfidenceMin("");
    setAppliedConfidenceMax("");
    setRiskDateFrom("");
    setRiskDateTo("");
    setSourceDateFrom("");
    setSourceDateTo("");
    setTriggeredByFilter(false);
    setTriggeredByFilterValue([]);
    setTriggeredByFilterMode("include");
    setPatientIdFilterText("");
    setAppliedPatientId("");
    setAccountCodeFilter(false);
    setAccountCodeFilterValue([]);
    setAccountCodeFilterMode("include");
    setPageNum(1);
  };

  const riskLevelOptions = [
    { label: "Life Threatening Risk", value: 1 },
    { label: "Very High Risk", value: 2 },
    { label: "High Risk", value: 3 },
    { label: "Moderate Risk", value: 4 },
    { label: "Low Risk", value: 5 },
  ];

  const evaluationStatusOptions = [
    { label: "Pending", value: 1 },
    { label: "Under Review", value: 2 },
    { label: "Correct", value: 3 },
    { label: "Incorrect", value: 4 },
  ];


  // Stats
  const [stats, setStats] = useState({
    totalPatients: 0,
    lifeThreatening: 0,
    veryHighRisk: 0,
    pendingReviews: 0,
    aiAccuracy: 0,
    falsePositive: 0,
    falseNegative: 0,
    evaluatedCount: 0,
    falsePositiveCount: 0,
    falseNegativeCount: 0,
  });

  useEffect(() => {
    fetchDashboard();
    setComponentMounted(true);
  }, []);

  // Parent bumps refreshToken when the user returns from a detail page where
  // they actually saved changes. Refetch with current filters intact so stale
  // rows update without disturbing pagination/scroll/filter state.
  useEffect(() => {
    if (!componentMounted) return;
    if (!props.refreshToken) return;
    fetchDashboard();
  }, [props.refreshToken]);

  const loadAccountCodeOptions = async (q, offset, limit) => {
    const res = await riskStratificationService.fetchAccountCodes({
      q,
      offset,
      limit,
    });
    return res || { options: [], total: 0, hasMore: false };
  };

  const loadEvaluatorOptions = async (q, offset, limit) => {
    const res = await riskStratificationService.fetchEvaluators({
      q,
      offset,
      limit,
    });
    return res || { options: [], total: 0, hasMore: false };
  };

  const loadTriggerReasonOptions = async (q, offset, limit) => {
    const res = await riskStratificationService.fetchTriggerReasons({
      q,
      offset,
      limit,
    });
    return res || { options: [], total: 0, hasMore: false };
  };

  // Single source of truth for filter-driven fetches. Every applied filter is a
  // dep — clearing a filter changes the state, this effect fires, fetch runs
  // with the up-to-date values (no stale-closure trap from inline fetches).
  // Date ranges are skipped while half-set (only From or only To) so we don't
  // round-trip on a partial pick.
  useEffect(() => {
    if (!componentMounted) return;
    if (!!riskDateFrom !== !!riskDateTo) return;
    if (!!sourceDateFrom !== !!sourceDateTo) return;
    fetchDashboard();
  }, [
    pageNum,
    riskLevelFilterValue,
    evaluationStatusFilterValue,
    triggeredByFilterValue,
    accountCodeFilterValue,
    evaluatorFilterValue,
    appliedPatientId,
    appliedConfidenceMin,
    appliedConfidenceMax,
    riskDateFrom,
    riskDateTo,
    sourceDateFrom,
    sourceDateTo,
  ]);

  // Wraps a multi-select filter in the {mode, values} shape the BE expects.
  // Returns null when there are no values — the caller omits that filter from
  // the body so empty filters never travel on the wire.
  const filterClause = (mode, values) => {
    if (!values || values.length === 0) return null;
    return { mode, values };
  };

  const buildFilterBody = ({ paginate } = { paginate: true }) => {
    const body = paginate ? { pageNum, pageSize } : {};

    const riskLevels = filterClause(
      riskLevelFilterMode,
      riskLevelFilterValue.map((o) => o.value),
    );
    if (riskLevels) body.riskLevels = riskLevels;

    const evaluationStatuses = filterClause(
      "include",
      evaluationStatusFilterValue.map((o) => o.value),
    );
    if (evaluationStatuses) body.evaluationStatuses = evaluationStatuses;

    const evaluatorIds = filterClause(
      evaluatorFilterMode,
      evaluatorFilterValue.map((o) => Number(o.value)),
    );
    if (evaluatorIds) body.evaluatorIds = evaluatorIds;

    const triggeredBy = filterClause(
      triggeredByFilterMode,
      triggeredByFilterValue.map((o) => o.value),
    );
    if (triggeredBy) body.riskLevelTriggeredBy = triggeredBy;

    if (riskDateFrom && riskDateTo) {
      body.riskStratificationDate = {
        from: new Date(riskDateFrom).toISOString(),
        to: new Date(riskDateTo).toISOString(),
      };
    }
    if (sourceDateFrom && sourceDateTo) {
      body.sourceDataDate = {
        from: new Date(sourceDateFrom).toISOString(),
        to: new Date(sourceDateTo).toISOString(),
      };
    }
    if (appliedConfidenceMin && appliedConfidenceMax) {
      body.aiConfidenceScore = {
        min: parseFloat(appliedConfidenceMin),
        max: parseFloat(appliedConfidenceMax),
      };
    }
    if (appliedPatientId.trim()) {
      body.patientId = parseInt(appliedPatientId.trim(), 10);
    }
    const accountCodes = filterClause(
      accountCodeFilterMode,
      accountCodeFilterValue.map((o) => o.value),
    );
    if (accountCodes) body.accountCodes = accountCodes;

    return body;
  };

  const fetchDashboard = () => {
    setLoading(true);
    const body = buildFilterBody({ paginate: true });

    riskStratificationService.fetchDashboard(
      body,
      (res) => {
        const data = res.data || res;
        const list = data.response || [];
        const total = data.totalCount || 0;

        setRecords(list);
        setTotalCount(total);
        fetchStats(total);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching risk dashboard:", err);
        EventService.emit("showError", {
          message: "Failed to fetch risk stratification data",
        });
        setLoading(false);
      },
    );
  };

  const fetchStats = (total) => {
    riskStratificationService.fetchDashboardStats(
      buildFilterBody({ paginate: false }),
      (res) => {
        const data = res.data || res;
        const evaluatedCount = data.evaluatedCount || 0;
        const correctCount = data.correctCount || 0;
        const fpCount = data.falsePositiveCount || 0;
        const fnCount = data.falseNegativeCount || 0;
        const falsePositive =
          evaluatedCount > 0
            ? ((fpCount / evaluatedCount) * 100).toFixed(1)
            : 0;
        const falseNegative =
          evaluatedCount > 0
            ? ((fnCount / evaluatedCount) * 100).toFixed(1)
            : 0;
        const aiAccuracy =
          evaluatedCount > 0
            ? ((correctCount / evaluatedCount) * 100).toFixed(1)
            : 0;
        setStats((prev) => ({
          ...prev,
          totalPatients: typeof total === "number" ? total : prev.totalPatients,
          lifeThreatening: data.lifeThreateningCount || 0,
          veryHighRisk: data.veryHighCount || 0,
          pendingReviews: data.pendingCount || 0,
          aiAccuracy,
          evaluatedCount,
          falsePositiveCount: fpCount,
          falseNegativeCount: fnCount,
          falsePositive,
          falseNegative,
        }));
      },
      (err) => {
        console.error("Error fetching dashboard stats:", err);
      },
    );
  };

  const handleDateFilter = () => {
    setPageNum(1);
    fetchDashboard();
  };

  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    riskStratificationService
      .exportDashboard(buildFilterBody({ paginate: true }))
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Export failed:", err);
        EventService.emit("showError", {
          message: "Failed to export. Please try again.",
        });
      })
      .finally(() => {
        setExporting(false);
      });
  };

  const numFrom = (pageNum - 1) * pageSize + 1;
  const numTo = Math.min(pageNum * pageSize, totalCount);

  return (
    <div className="risk-dashboard-container">
      {/* Header */}
      <div className="risk-dashboard-header">
        <img
          src={backArrowIcon}
          alt="back"
          className="risk-dashboard-back-btn"
          onClick={() => props.history && props.history.goBack()}
        />
        <h1>Risk stratification dashboard</h1>
      </div>

      {/* Controls Row */}
      <div className="risk-dashboard-controls">
        <div className="risk-dashboard-date-filter">
          <div
            className="risk-date-range-picker"
            onClick={() => {
              if (!riskDateFrom && !riskDateTo) {
                setOpenBackdrop(true);
                setOpenFilter(10);
              }
            }}
          >
            <FontIcon className="risk-date-calendar-icon">date_range</FontIcon>
            {riskDateFrom && riskDateTo ? (
              <>
                <span className="risk-date-selected-text">
                  {moment(riskDateFrom).format("DD MMM YYYY")} –{" "}
                  {moment(riskDateTo).format("DD MMM YYYY")}
                </span>
                <span
                  className="risk-date-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRiskDateFrom("");
                    setRiskDateTo("");
                    setPageNum(1);
                  }}
                >
                  ✕
                </span>
              </>
            ) : (
              <span className="risk-date-placeholder">Filter by date</span>
            )}
          </div>
          {openFilter === 10 && (
            <div className="risk-date-popup">
              <div className="risk-date-popup-row">
                <label>From</label>
                <input
                  type="date"
                  value={riskDateFrom}
                  onChange={(e) => setRiskDateFrom(e.target.value)}
                />
              </div>
              <div className="risk-date-popup-row">
                <label>To</label>
                <input
                  type="date"
                  value={riskDateTo}
                  min={riskDateFrom || undefined}
                  onChange={(e) => {
                    if (
                      riskDateFrom &&
                      e.target.value &&
                      e.target.value >= riskDateFrom
                    ) {
                      setRiskDateTo(e.target.value);
                      setOpenFilter(0);
                      setOpenBackdrop(false);
                      setPageNum(1);
                    } else if (!riskDateFrom) {
                      setRiskDateTo(e.target.value);
                    }
                  }}
                />
              </div>
              {riskDateFrom && !riskDateTo && (
                <p className="risk-date-popup-hint">Now select end date</p>
              )}
            </div>
          )}
        </div>
        <button
          className="risk-dashboard-export-btn"
          onClick={handleExport}
          disabled={exporting}
        >
          <img src={exportIcon} alt="export" />{" "}
          {exporting ? "Exporting..." : "Export"}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="risk-dashboard-stats">
        <div className="risk-stat-card">
          <div className="risk-stat-label">Total Patients</div>
          <div className="risk-stat-value">{stats.totalPatients}</div>
        </div>
        <div className="risk-stat-card">
          <div className="risk-stat-label">Life Threatening Risk</div>
          <div className="risk-stat-value life-threatening">
            {stats.lifeThreatening}
          </div>
        </div>
        <div className="risk-stat-card">
          <div className="risk-stat-label">Very High Risk</div>
          <div className="risk-stat-value very-high">{stats.veryHighRisk}</div>
        </div>
        <div className="risk-stat-card">
          <div className="risk-stat-label">Pending Reviews</div>
          <div className="risk-stat-value pending">{stats.pendingReviews}</div>
        </div>
        <div className="risk-stat-card">
          <div className="risk-stat-label">AI Accuracy</div>
          <div className="risk-stat-value accuracy">{stats.aiAccuracy}%</div>
        </div>
        <div className="risk-stat-card">
          <div className="risk-stat-label">False Positive</div>
          <div className="risk-stat-value false-positive">
            {stats.falsePositive}%
          </div>
          <div className="risk-stat-subtitle">
            {stats.evaluatedCount > 0
              ? `${stats.falsePositiveCount} / ${stats.evaluatedCount} reviewed`
              : "No reviews yet"}
          </div>
        </div>
        <div className="risk-stat-card">
          <div className="risk-stat-label">False Negative</div>
          <div className="risk-stat-value false-negative">
            {stats.falseNegative}%
          </div>
          <div className="risk-stat-subtitle">
            {stats.evaluatedCount > 0
              ? `${stats.falseNegativeCount} / ${stats.evaluatedCount} reviewed`
              : "No reviews yet"}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <GlobalSkeletonLoader />
      ) : (
        <div className="risk-dashboard-table-container">
          <table>
            <thead>
              <tr>
                <th className="filter-th">
                  <span>Patient ID</span>
                  <img
                    src={appliedPatientId ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(8);
                    }}
                  />
                  {openFilter === 8 && (
                    <div
                      className="risk-filter-popup risk-filter-search-popup"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TextField
                        placeholder="Patient ID"
                        value={patientIdFilterText}
                        onChange={(e) => {
                          let val = e.target.value.replace(/[^0-9]/g, "");
                          if (val.length > 15) val = val.slice(0, 15);
                          setPatientIdFilterText(val);
                        }}
                        InputProps={{
                          endAdornment: patientIdFilterText && (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  setPatientIdFilterText("");
                                  setAppliedPatientId("");
                                  setPageNum(1);
                                }}
                                edge="end"
                                size="small"
                              >
                                <FontIcon>close</FontIcon>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          cursor: "pointer",
                          marginLeft: 8,
                        }}
                        onClick={() => {
                          const trimmed = patientIdFilterText.trim();
                          if (trimmed) {
                            setAppliedPatientId(trimmed);
                            setPageNum(1);
                          }
                        }}
                      >
                        <img
                          src={goIcon}
                          alt="Go"
                          style={{ width: 20, height: 28, cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  )}
                </th>
                <th className="filter-th">
                  <span>Account code</span>
                  <img
                    ref={accountCodeFilterIconRef}
                    src={accountCodeFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(9);
                    }}
                  />
                  {openFilter === 9 && (
                    <ColumnFilterPopover
                      anchorRef={accountCodeFilterIconRef}
                      title="Filter by Account Code"
                      searchPlaceholder="Search account codes..."
                      loadOptions={loadAccountCodeOptions}
                      selectedIds={accountCodeFilterValue.map((o) => o.value)}
                      selectedItems={accountCodeFilterValue.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      mode={accountCodeFilterMode}
                      onApply={(ids, items, mode) => {
                        const next = items.map((it) => ({
                          value: it.id,
                          label: it.label,
                        }));
                        setAccountCodeFilterValue(next);
                        setAccountCodeFilterMode(mode);
                        setAccountCodeFilter(next.length > 0);
                        setPageNum(1);
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                      onClose={() => {
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                    />
                  )}
                </th>
                <th className="filter-th">
                  <span>Risk stratification date</span>
                  <img
                    src={riskDateFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(1);
                    }}
                  />
                  {openFilter === 1 && (
                    <div className="risk-filter-popup">
                      <div className="risk-filter-date-row">
                        <p>From</p>
                        <TextField
                          type="date"
                          style={{ width: "100%" }}
                          onChange={(e) => setRiskDateFrom(e.target.value)}
                          value={riskDateFrom}
                        />
                      </div>
                      <div className="risk-filter-date-row">
                        <p>To</p>
                        <TextField
                          type="date"
                          style={{ width: "100%" }}
                          onChange={(e) => setRiskDateTo(e.target.value)}
                          value={riskDateTo}
                        />
                      </div>
                      <Button
                        onClick={() => {
                          setRiskDateFrom("");
                          setRiskDateTo("");
                        }}
                      >
                        <FontIcon>close</FontIcon>
                      </Button>
                    </div>
                  )}
                </th>
                <th className="filter-th">
                  <span>Source data date</span>
                  <img
                    src={sourceDateFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(2);
                    }}
                  />
                  {openFilter === 2 && (
                    <div className="risk-filter-popup">
                      <div className="risk-filter-date-row">
                        <p>From</p>
                        <TextField
                          type="date"
                          style={{ width: "100%" }}
                          onChange={(e) => setSourceDateFrom(e.target.value)}
                          value={sourceDateFrom}
                        />
                      </div>
                      <div className="risk-filter-date-row">
                        <p>To</p>
                        <TextField
                          type="date"
                          style={{ width: "100%" }}
                          onChange={(e) => setSourceDateTo(e.target.value)}
                          value={sourceDateTo}
                        />
                      </div>
                      <Button
                        onClick={() => {
                          setSourceDateFrom("");
                          setSourceDateTo("");
                        }}
                      >
                        <FontIcon>close</FontIcon>
                      </Button>
                    </div>
                  )}
                </th>
                <th className="filter-th">
                  <span>Risk level</span>
                  <img
                    ref={riskLevelFilterIconRef}
                    src={riskLevelFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(3);
                    }}
                  />
                  {openFilter === 3 && (
                    <ColumnFilterPopover
                      anchorRef={riskLevelFilterIconRef}
                      title="Filter by Risk Level"
                      searchPlaceholder="Search risk levels..."
                      options={riskLevelOptions.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      selectedIds={riskLevelFilterValue.map((o) => o.value)}
                      mode={riskLevelFilterMode}
                      onApply={(ids, items, mode) => {
                        const next = riskLevelOptions.filter((o) =>
                          ids.includes(o.value),
                        );
                        setRiskLevelFilterValue(next);
                        setRiskLevelFilterMode(mode);
                        setRiskLevelFilter(next.length > 0);
                        setPageNum(1);
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                      onClose={() => {
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                    />
                  )}
                </th>
                <th className="filter-th">
                  <span>AI Confidence</span>
                  <img
                    src={confidenceFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(4);
                    }}
                  />
                  {openFilter === 4 && (
                    <div className="risk-filter-popup risk-filter-range-popup">
                      <RangeSlider
                        min={confidenceMin ? Number(confidenceMin) : 0}
                        max={confidenceMax ? Number(confidenceMax) : 100}
                        rangeMin={0}
                        rangeMax={100}
                        setAge={(min, max) => {
                          setConfidenceMin(String(min));
                          setConfidenceMax(String(max));
                        }}
                      />
                      <div className="risk-filter-range-label">
                        {`${confidenceMin || 0}% - ${confidenceMax || 100}%`}
                      </div>
                      <div className="risk-filter-range-actions">
                        <Button
                          className="risk-filter-apply-btn"
                          flat
                          onClick={() => {
                            const min = Number(confidenceMin) || 0;
                            const max = Number(confidenceMax) || 100;
                            if (min >= 0 && max <= 100 && min < max) {
                              setConfidenceFilter(true);
                              setAppliedConfidenceMin(String(min));
                              setAppliedConfidenceMax(String(max));
                              setOpenFilter(0);
                              setOpenBackdrop(false);
                              setPageNum(1);
                            }
                          }}
                        >
                          Apply
                        </Button>
                        <Button
                          className="risk-filter-clear-btn"
                          flat
                          onClick={() => {
                            setConfidenceMin("");
                            setConfidenceMax("");
                            setAppliedConfidenceMin("");
                            setAppliedConfidenceMax("");
                            setConfidenceFilter(false);
                            setOpenFilter(0);
                            setOpenBackdrop(false);
                            setPageNum(1);
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </th>
                <th className="filter-th">
                  <span>Risk level triggered by</span>
                  <img
                    ref={triggeredByFilterIconRef}
                    src={triggeredByFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(5);
                    }}
                  />
                  {openFilter === 5 && (
                    <ColumnFilterPopover
                      anchorRef={triggeredByFilterIconRef}
                      title="Filter by Risk Level Triggered By"
                      searchPlaceholder="Search triggers..."
                      loadOptions={loadTriggerReasonOptions}
                      selectedIds={triggeredByFilterValue.map((o) => o.value)}
                      selectedItems={triggeredByFilterValue.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      mode={triggeredByFilterMode}
                      onApply={(ids, items, mode) => {
                        const next = items.map((it) => ({
                          value: it.id,
                          label: it.label,
                        }));
                        setTriggeredByFilterValue(next);
                        setTriggeredByFilterMode(mode);
                        setTriggeredByFilter(next.length > 0);
                        setPageNum(1);
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                      onClose={() => {
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                    />
                  )}
                </th>
                <th className="filter-th">
                  <span>Evaluation status</span>
                  <img
                    src={evaluationStatusFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(6);
                    }}
                  />
                  {openFilter === 6 && (
                    <div className="risk-filter-popup">
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={evaluationStatusOptions}
                        value={evaluationStatusFilterValue}
                        onChange={(e, v) => {
                          setEvaluationStatusFilterValue(v);
                          setEvaluationStatusFilter(v.length > 0);
                        }}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select status" />
                        )}
                      />
                    </div>
                  )}
                </th>
                <th className="filter-th">
                  <span>Evaluator</span>
                  <img
                    ref={evaluatorFilterIconRef}
                    src={evaluatorFilter ? filterApplied : funnelIcon}
                    className="filter-icon"
                    onClick={() => {
                      setOpenBackdrop(true);
                      setOpenFilter(7);
                    }}
                  />
                  {openFilter === 7 && (
                    <ColumnFilterPopover
                      anchorRef={evaluatorFilterIconRef}
                      title="Filter by Evaluator"
                      searchPlaceholder="Search evaluators..."
                      loadOptions={loadEvaluatorOptions}
                      selectedIds={evaluatorFilterValue.map((o) => o.value)}
                      selectedItems={evaluatorFilterValue.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      mode={evaluatorFilterMode}
                      onApply={(ids, items, mode) => {
                        const next = items.map((it) => ({
                          value: it.id,
                          label: it.label,
                        }));
                        setEvaluatorFilterValue(next);
                        setEvaluatorFilterMode(mode);
                        setEvaluatorFilter(next.length > 0);
                        setPageNum(1);
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                      onClose={() => {
                        setOpenFilter(0);
                        setOpenBackdrop(false);
                      }}
                    />
                  )}
                </th>
                <th className="reset-filters-th">
                  <button
                    type="button"
                    className="reset-filters-btn"
                    onClick={resetAllFilters}
                    disabled={!hasActiveFilters}
                  >
                    <span className="reset-filters-label">Reset filters</span>
                    <FontIcon className="reset-filters-icon">close</FontIcon>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && (
                <tr className="no-records-row">
                  <td className="no-records-cell">No records found</td>
                </tr>
              )}
              {records.map((record, index) => {
                const aiRiskLevel = record.riskLevel;
                const displayedRiskLevel =
                  record.updatedRiskLevel || record.riskLevel;
                const displayedRiskClass =
                  RISK_LEVEL_CLASS_MAP[displayedRiskLevel] || "moderate";
                const aiRiskClass =
                  RISK_LEVEL_CLASS_MAP[aiRiskLevel] || "moderate";
                const awaitingEval = isAwaitingEvaluation(
                  record.evaluationStatus,
                );
                const confidenceScore = record.aiConfidenceScore || 0;
                const confClass = getConfidenceClass(confidenceScore);
                const evalStatusClass = getEvaluationStatusClass(
                  record.evaluationStatus,
                );
                const evaluatorName = record.evaluatedByName || "";

                return (
                  <tr key={record.id || index}>
                    <td>
                      {record.patientId ? (
                        <Link
                          to={`/patient-record/${record.patientId}`}
                          className="patient-id-link"
                        >
                          {record.patientId}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                      <td>{record.accountCode || "-"}</td>
                      <td>
                        {record.completedAt
                          ? moment(record.completedAt).format("DD MMM YYYY")
                          : "-"}
                      </td>
                      <td>
                        {record.appointmentDate
                          ? moment(record.appointmentDate).format("DD MMM YYYY")
                          : "-"}
                      </td>
                      <td className="risk-level-cell">
                        <span
                          className={`risk-level-badge ${displayedRiskClass}`}
                        >
                          {awaitingEval ? (
                            <img
                              src={starRedIcon}
                              alt=""
                              className="risk-level-icon"
                            />
                          ) : (
                            <AiAssessmentTooltip
                              arrow
                              placement="top"
                              title={
                                <span>
                                  <span style={{ color: "#fff" }}>
                                    AI assessment:{" "}
                                  </span>
                                  <span
                                    className={`ai-tooltip-risk ${aiRiskClass}`}
                                  >
                                    {RISK_LEVEL_LABEL_MAP[aiRiskLevel] || "-"}
                                  </span>
                                </span>
                              }
                            >
                              <img
                                src={personOrangeIcon}
                                alt="AI assessment"
                                className="risk-level-icon hoverable"
                              />
                            </AiAssessmentTooltip>
                          )}
                          <span className="risk-level-text">
                            <span className="risk-level-name">
                              {RISK_LEVEL_LABEL_MAP[displayedRiskLevel] || "-"}
                            </span>
                            {record.riskAnalysisVersion ? (
                              <span className="risk-version">
                                v{record.riskAnalysisVersion}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className={`confidence-score ${confClass}`}>
                          {confidenceScore}%
                        </span>
                      </td>
                      <td>
                        {Array.isArray(record.riskLevelTriggeredBy) &&
                        record.riskLevelTriggeredBy.length > 0
                          ? record.riskLevelTriggeredBy.join(", ")
                          : "-"}
                      </td>
                      <td>
                        <span
                          className={`evaluation-status-badge ${evalStatusClass}`}
                        >
                          {getEvaluationStatusLabel(record.evaluationStatus)}
                        </span>
                      </td>
                      <td>
                        {!record.evaluatedBy ? (
                          <button
                            className="evaluate-cta-btn"
                            disabled={assigning}
                            onClick={() => handleEvaluateClick(record)}
                          >
                            {assigning ? "Assigning..." : "Evaluate"}
                          </button>
                        ) : (
                          <span
                            className="evaluator-name-pill clickable"
                            onClick={() => handleEvaluatorPillClick(record)}
                          >
                            {evaluatorName || "-"}
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() =>
                            navigateToDetail(record)
                          }
                        >
                          <img
                            src={eyeOrangeIcon}
                            alt="view"
                            style={{ width: 16, height: 16 }}
                          />
                        </button>
                      </td>
                    </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="risk-dashboard-pagination">
            <Button
              className="pagination-btn"
              disabled={pageNum <= 1 || loading}
              onClick={() => setPageNum(pageNum - 1)}
            >
              <FontIcon className="pagination-icon">chevron_left</FontIcon>
            </Button>
            <div className="pagination-info">
              Showing&nbsp;
              <span className="pagination-range">
                {numFrom}&nbsp;-&nbsp;{numTo}
              </span>
              <span>&nbsp;of&nbsp;</span>
              <span>{totalCount}</span>
            </div>
            <Button
              className="pagination-btn"
              disabled={loading || pageNum * pageSize >= totalCount}
              onClick={() => setPageNum(pageNum + 1)}
            >
              <FontIcon className="pagination-icon">chevron_right</FontIcon>
            </Button>
          </div>
        </div>
      )}
      {openBackdrop && (
        <div
          className="risk-filter-backdrop"
          onClick={() => {
            setOpenFilter(0);
            setOpenBackdrop(false);
          }}
        ></div>
      )}

      {assignDialogRecord && (
        <div
          className="assign-evaluator-overlay"
          onClick={() => !assigning && setAssignDialogRecord(null)}
        >
          <div
            className="assign-evaluator-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="assign-evaluator-icon-wrap">
              <span className="assign-evaluator-icon">!</span>
            </div>
            <h3 className="assign-evaluator-title">
              Assign yourself as the evaluator?
            </h3>
            <p className="assign-evaluator-body">
              By confirming, you'll take over as the evaluator for this AI
              summary. From this point onward:
            </p>
            <ul className="assign-evaluator-list">
              <li>All evaluations on this summary will be attributed to you</li>
              <li>
                This action is permanent — once assigned, the evaluator cannot
                be changed
              </li>
            </ul>
            <div className="assign-evaluator-actions">
              <button
                className="assign-evaluator-cancel"
                disabled={assigning}
                onClick={() => setAssignDialogRecord(null)}
              >
                Cancel
              </button>
              <button
                className="assign-evaluator-confirm"
                disabled={assigning}
                onClick={handleConfirmReassign}
              >
                {assigning ? "Assigning..." : "Yes, assign to me"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RiskDashboard;
